import React, { useState, useCallback, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { DashboardLayout } from './Dashboard';
import Tour from '../components/Tour';
import NotionExportButton from '../components/NotionExportButton';
import TrelloExportButton from '../components/TrelloExportButton';
import ClickUpExportButton from '../components/ClickUpExportButton';
import FunnelFlowchart, { autoBuildToStages } from '../components/FunnelFlowchart';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import {
  Wrench, Sparkle, Target, MagnifyingGlass, Translate, Megaphone, FileText, Envelope,
  Download, Copy, Lightning, CaretRight, ArrowRight, Tag, ArrowUpRight, Crown,
  CheckCircle, Star, Trash, Code,
} from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LANGUAGES = [
  { code: 'pt', label: 'Português (PT + BR)' },
  { code: 'es', label: 'Español' },
  { code: 'en', label: 'English' },
];

const TONES = [
  { code: 'persuasivo', label: 'Persuasivo' },
  { code: 'profissional', label: 'Profissional' },
  { code: 'descontraido', label: 'Descontraído' },
  { code: 'cientifico', label: 'Científico' },
];

const STAGE_BADGES = {
  welcome: { label: 'Boas-vindas', color: '#10B981' },
  story: { label: 'História', color: '#A855F7' },
  value: { label: 'Valor', color: '#06B6D4' },
  soft_pitch: { label: 'Soft Pitch', color: '#F59E0B' },
  hard_pitch: { label: 'Hard Pitch', color: '#EF4444' },
  urgency: { label: 'Urgência', color: '#EC4899' },
  last_call: { label: 'Last Call', color: '#6366F1' },
};

const copyToClipboard = async (text, label) => {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(label || i18n.t('common.copied'));
  } catch {
    toast.error(i18n.t('common.copy_fail'));
  }
};

const downloadJson = (data, filename) => {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

const downloadMarkdown = (build) => {
  const lm = build.lead_magnet || {};
  const lp = build.landing_copy || {};
  const em = build.email_sequence || {};
  const md = `# Funnel Auto-Build · ${build.niche}\n\n**Oferta**: ${build.offer}\n\n---\n\n## 🧲 Lead Magnet\n\n` +
    `**Formato**: ${lm.format || '-'}\n\n**Título**: ${lm.title || '-'}\n\n**Subtítulo**: ${lm.subtitle || '-'}\n\n` +
    `**Promessa**: ${lm.promise || '-'}\n\n**Estrutura**:\n${(lm.structure || []).map((s, i) => `${i + 1}. ${s}`).join('\n')}\n\n` +
    `**Hook**: ${lm.hook_headline || '-'}\n\n**Bullets**:\n${(lm.bullets || []).map((b) => `- ${b}`).join('\n')}\n\n` +
    `**CTA**: ${lm.cta_button || '-'}\n\n**Thank-you**: ${lm.thank_you_message || '-'}\n\n---\n\n## 📄 Landing Page\n\n` +
    `### Hero\n**H1**: ${lp.hero?.h1 || '-'}\n**Sub**: ${lp.hero?.subheadline || '-'}\n**CTA**: ${lp.hero?.cta_primary || '-'}\n\n` +
    `### Problem\n${lp.problem_agitation || '-'}\n\n### Solução\n${lp.solution_intro || '-'}\n\n### Benefícios\n` +
    (lp.benefits || []).map((b) => `- **${b.title}**: ${b.description}`).join('\n') + `\n\n### Como funciona\n` +
    (lp.how_it_works || []).map((s) => `${s.step}. **${s.title}** — ${s.description}`).join('\n') + `\n\n### Prova social\n${lp.social_proof?.headline || ''}\n` +
    (lp.social_proof?.testimonials || []).map((t) => `> "${t.quote}" — **${t.name}**, ${t.role}`).join('\n\n') + `\n\n### Garantia\n**${lp.guarantee?.title || ''}** — ${lp.guarantee?.body || ''}\n\n### FAQ\n` +
    (lp.faq || []).map((f) => `**${f.q}**\n${f.a}`).join('\n\n') + `\n\n### CTA Final\n**${lp.final_cta?.headline || ''}** — ${lp.final_cta?.subheadline || ''}\n**Botão**: ${lp.final_cta?.button || ''}\n\n---\n\n## ✉️ Sequência de Emails\n\n` +
    `**Estratégia**: ${em.sequence_strategy || '-'}\n\n` +
    (em.emails || []).map((e) => `### Email ${(e.day ?? 0)} · ${e.stage}\n**Assunto**: ${e.subject}\n**Preview**: ${e.preview}\n\n${e.body}\n`).join('\n---\n\n');
  const blob = new Blob([md], { type: 'text/markdown' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `auto-build-${build.build_id}.md`;
  a.click();
  URL.revokeObjectURL(url);
};

// ─── UI Subcomponents ───────────────────────────────────────────────

const SectionCard = ({ icon: Icon, title, color = '#A855F7', children, actions }) => (
  <div className="p-5 bg-[#121212] border border-white/10 rounded-2xl">
    <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${color}22` }}>
          <Icon size={16} weight="bold" style={{ color }} />
        </div>
        <h3 className="font-[Outfit] font-bold">{title}</h3>
      </div>
      {actions}
    </div>
    {children}
  </div>
);

const KeyVal = ({ k, v }) => (
  <div className="py-2 border-b border-white/5 last:border-b-0">
    <div className="text-[10px] uppercase tracking-wider text-white/40 mb-0.5">{k}</div>
    <div className="text-sm text-white/90 leading-relaxed">{v || <span className="text-white/30">—</span>}</div>
  </div>
);

const LeadMagnetView = ({ data }) => {
  if (!data) return null;
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="px-2.5 py-1 bg-[#10B981]/15 border border-[#10B981]/30 rounded text-[10px] uppercase tracking-wider text-[#10B981] font-semibold">
          {data.format || 'Lead Magnet'}
        </span>
      </div>
      <KeyVal k="Título" v={data.title} />
      <KeyVal k="Subtítulo" v={data.subtitle} />
      <KeyVal k="Promessa" v={data.promise} />
      {Array.isArray(data.structure) && data.structure.length > 0 && (
        <div className="py-2 border-b border-white/5">
          <div className="text-[10px] uppercase tracking-wider text-white/40 mb-1.5">Estrutura</div>
          <ol className="space-y-1 list-decimal pl-5 text-sm text-white/80">
            {data.structure.map((s, i) => <li key={i}>{s}</li>)}
          </ol>
        </div>
      )}
      <KeyVal k="Hook (opt-in headline)" v={data.hook_headline} />
      {Array.isArray(data.bullets) && data.bullets.length > 0 && (
        <div className="py-2 border-b border-white/5">
          <div className="text-[10px] uppercase tracking-wider text-white/40 mb-1.5">Bullets</div>
          <ul className="space-y-1.5 text-sm text-white/80">
            {data.bullets.map((b, i) => (
              <li key={i} className="flex items-start gap-2">
                <CheckCircle size={14} weight="fill" className="text-[#10B981] flex-shrink-0 mt-0.5" />
                <span>{b}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      <KeyVal k="CTA Button" v={data.cta_button} />
      <KeyVal k="Thank-You Message" v={data.thank_you_message} />
    </div>
  );
};

const LandingView = ({ data }) => {
  if (!data) return null;
  return (
    <div className="space-y-4">
      {/* Hero */}
      <div className="p-4 bg-gradient-to-br from-[#2563EB]/15 to-transparent border border-[#2563EB]/30 rounded-xl">
        <div className="text-[10px] uppercase tracking-wider text-[#60A5FA] font-semibold mb-2">HERO</div>
        <h2 className="text-2xl font-[Outfit] font-bold leading-tight mb-2">{data.hero?.h1}</h2>
        <p className="text-white/70 mb-3 leading-relaxed">{data.hero?.subheadline}</p>
        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#2563EB] rounded-lg text-sm font-semibold">
          {data.hero?.cta_primary} <ArrowRight size={14} weight="bold" />
        </span>
      </div>

      {/* Problem & Solution */}
      <KeyVal k="Problema (agitação)" v={data.problem_agitation} />
      <KeyVal k="Introdução à solução" v={data.solution_intro} />

      {/* Benefits */}
      {Array.isArray(data.benefits) && data.benefits.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-wider text-white/40 mb-2">Benefícios</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
            {data.benefits.map((b, i) => (
              <div key={i} className="p-3 bg-white/5 border border-white/10 rounded-lg">
                <div className="font-semibold text-sm mb-1">{b.title}</div>
                <div className="text-xs text-white/60">{b.description}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* How it works */}
      {Array.isArray(data.how_it_works) && data.how_it_works.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-wider text-white/40 mb-2">Como funciona</div>
          <div className="space-y-2">
            {data.how_it_works.map((s, i) => (
              <div key={i} className="flex gap-3 p-3 bg-white/5 border border-white/10 rounded-lg">
                <div className="w-7 h-7 rounded-full bg-[#A855F7] flex items-center justify-center font-bold text-sm flex-shrink-0">{s.step}</div>
                <div>
                  <div className="font-semibold text-sm">{s.title}</div>
                  <div className="text-xs text-white/60">{s.description}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Social proof */}
      {data.social_proof?.testimonials && data.social_proof.testimonials.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-wider text-white/40 mb-2">Prova Social</div>
          <div className="font-semibold text-sm mb-2">{data.social_proof.headline}</div>
          <div className="space-y-2">
            {data.social_proof.testimonials.map((t, i) => (
              <div key={i} className="p-3 bg-white/5 border-l-2 border-[#F59E0B] rounded-r-lg">
                <p className="text-sm text-white/85 italic mb-1.5">&ldquo;{t.quote}&rdquo;</p>
                <div className="text-xs text-white/50">— <strong className="text-white/70">{t.name}</strong>, {t.role}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Guarantee */}
      {data.guarantee && (
        <div className="p-4 bg-gradient-to-br from-[#10B981]/10 to-transparent border border-[#10B981]/30 rounded-xl">
          <div className="flex items-center gap-2 mb-1.5">
            <Crown size={16} weight="fill" className="text-[#10B981]" />
            <span className="font-[Outfit] font-bold">{data.guarantee.title}</span>
          </div>
          <p className="text-sm text-white/70">{data.guarantee.body}</p>
        </div>
      )}

      {/* FAQ */}
      {Array.isArray(data.faq) && data.faq.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-wider text-white/40 mb-2">FAQ</div>
          <div className="space-y-2">
            {data.faq.map((f, i) => (
              <details key={i} className="p-3 bg-white/5 border border-white/10 rounded-lg group">
                <summary className="font-semibold text-sm cursor-pointer flex items-center justify-between">
                  <span>{f.q}</span>
                  <CaretRight size={14} className="group-open:rotate-90 transition" />
                </summary>
                <p className="text-sm text-white/70 mt-2 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      )}

      {/* Final CTA */}
      {data.final_cta && (
        <div className="p-4 bg-gradient-to-br from-[#A855F7]/15 to-[#7C3AED]/10 border border-[#A855F7]/30 rounded-xl text-center">
          <h3 className="font-[Outfit] font-bold text-lg mb-1">{data.final_cta.headline}</h3>
          <p className="text-sm text-white/70 mb-2">{data.final_cta.subheadline}</p>
          <span className="inline-flex items-center gap-1.5 px-4 py-2 bg-gradient-to-r from-[#A855F7] to-[#7C3AED] rounded-lg text-sm font-semibold">
            {data.final_cta.button} <ArrowRight size={14} weight="bold" />
          </span>
        </div>
      )}
    </div>
  );
};

const EmailsView = ({ data }) => {
  if (!data) return null;
  return (
    <div className="space-y-3">
      {data.sequence_strategy && (
        <div className="p-3 bg-white/5 border border-white/10 rounded-lg">
          <div className="text-[10px] uppercase tracking-wider text-white/40 mb-1">Estratégia da sequência</div>
          <p className="text-sm text-white/80 leading-relaxed">{data.sequence_strategy}</p>
        </div>
      )}
      {(data.emails || []).map((email, i) => {
        const stage = STAGE_BADGES[email.stage] || { label: email.stage || 'email', color: '#9CA3AF' };
        return (
          <div key={i} className="p-4 bg-[#0A0A0A] border border-white/10 rounded-xl" data-testid={`email-${i}`}>
            <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-[#A855F7]/15 flex items-center justify-center">
                  <Envelope size={14} weight="bold" className="text-[#A855F7]" />
                </div>
                <span className="font-[Outfit] font-bold">Email {i + 1}</span>
                <span className="text-[10px] uppercase tracking-wider text-white/50">Dia {email.day ?? '?'}</span>
                <span
                  className="px-2 py-0.5 rounded text-[10px] uppercase tracking-wider font-semibold"
                  style={{ background: `${stage.color}22`, color: stage.color }}
                >
                  {stage.label}
                </span>
              </div>
              <button
                onClick={() => copyToClipboard(`Assunto: ${email.subject}\nPreview: ${email.preview}\n\n${email.body}`, i18n.t('autobuild.email_copied'))}
                data-testid={`email-copy-${i}`}
                className="inline-flex items-center gap-1 px-2.5 py-1 bg-white/5 hover:bg-white/10 border border-white/10 rounded text-[10px] uppercase tracking-wider transition"
                title="Copiar email"
              >
                <Copy size={11} weight="bold" /> Copiar
              </button>
            </div>
            <div className="space-y-1.5">
              <div>
                <span className="text-[10px] uppercase tracking-wider text-white/40 mr-2">Assunto:</span>
                <span className="text-sm font-semibold">{email.subject}</span>
              </div>
              <div>
                <span className="text-[10px] uppercase tracking-wider text-white/40 mr-2">Preview:</span>
                <span className="text-xs text-white/60 italic">{email.preview}</span>
              </div>
              <div className="mt-2 p-3 bg-white/5 rounded text-sm text-white/85 whitespace-pre-wrap leading-relaxed">
                {email.body}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ─── Main page component ─────────────────────────────────────────────

const AutoBuild = ({ user }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const initialNiche = searchParams.get('niche') || '';
  const initialUrl = searchParams.get('url') || '';
  const initialOffer = searchParams.get('offer') || '';

  const [niche, setNiche] = useState(initialNiche);
  const [offer, setOffer] = useState(initialOffer);
  const [sourceUrl, setSourceUrl] = useState(initialUrl);
  const [language, setLanguage] = useState('pt');
  const [tone, setTone] = useState('persuasivo');
  const [loading, setLoading] = useState(false);
  const [build, setBuild] = useState(null);
  const [activeTab, setActiveTab] = useState('flow');
  const [history, setHistory] = useState([]);

  const loadHistory = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/auto-build/history`, { withCredentials: true });
      setHistory(res.data.items || []);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { loadHistory(); }, [loadHistory]);
  useEffect(() => {
    const h = () => loadHistory();
    window.addEventListener('brand:changed', h);
    return () => window.removeEventListener('brand:changed', h);
  }, [loadHistory]);

  const generate = useCallback(async (e) => {
    if (e) e.preventDefault();
    if (!niche.trim() || !offer.trim()) {
      toast.error(t('autobuild.fill_required'));
      return;
    }
    setLoading(true);
    setBuild(null);
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/auto-build/generate`,
        {
          niche: niche.trim(),
          offer: offer.trim(),
          source_url: sourceUrl.trim() || null,
          language,
          tone,
        },
        { withCredentials: true }
      );
      setBuild(res.data);
      setActiveTab('flow');
      toast.success(t('autobuild.success_toast'));
      loadHistory();
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Falha ao gerar o funil. Tenta novamente.';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, [niche, offer, sourceUrl, language, tone, loadHistory, t]);

  const openBuild = async (buildId) => {
    setLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/auto-build/${buildId}`, { withCredentials: true });
      setBuild(res.data);
      setNiche(res.data.niche);
      setOffer(res.data.offer);
      setSourceUrl(res.data.source_url || '');
      setLanguage(res.data.language || 'pt');
      setTone(res.data.tone || 'persuasivo');
      setActiveTab('flow');
    } catch {
      toast.error('Não foi possível abrir este build');
    } finally {
      setLoading(false);
    }
  };

  const deleteBuild = async (buildId) => {
    if (!window.confirm(t('autobuild.confirm_delete'))) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/auto-build/${buildId}`, { withCredentials: true });
      toast.success(t('autobuild.deleted'));
      if (build?.build_id === buildId) setBuild(null);
      loadHistory();
    } catch {
      toast.error(t('autobuild.delete_fail'));
    }
  };

  const tabs = useMemo(() => ([
    { id: 'flow', label: t('autobuild.tab_flow'), icon: Wrench, color: '#F59E0B' },
    { id: 'lead_magnet', label: t('autobuild.tab_lead_magnet'), icon: Star, color: '#10B981' },
    { id: 'landing', label: t('autobuild.tab_landing'), icon: FileText, color: '#2563EB' },
    { id: 'emails', label: t('autobuild.tab_emails'), icon: Envelope, color: '#A855F7' },
  ]), [t]);

  return (
    <DashboardLayout user={user} activePage="/auto-build">
      <Tour tourId="auto-build-v1" steps={[
        { target: '[data-testid="auto-build-niche-input"]', content: 'Nicho + audiência são os 2 inputs mais determinantes. Sê específico — "nutrição para mulheres em menopausa" gera melhor copy que "saúde".', disableBeacon: true },
        { target: '[data-testid="auto-build-offer-input"]', content: 'Descreve a oferta em 1-2 frases. A IA usa isto para gerar lead magnet, landing copy E sequência de 5 emails coerentes.' },
        { target: '[data-testid="auto-build-url-input"]', content: 'Opcional: cola um URL de concorrente para "clonar" estrutura. Cria pontes naturais entre o que funciona neles e o teu próprio ângulo.' },
        { target: '[data-testid="auto-build-generate-btn"]', content: 'Em ~40-60s tens funil completo: lead magnet · landing copy · 5 emails. Tens 1 build grátis vitalício.' },
      ]} />
      <div className="p-6" data-testid="auto-build-page">
        {/* Header */}
        <div className="mb-8">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#A855F7]/15 border border-[#A855F7]/30 rounded-full mb-3">
            <Wrench size={14} weight="fill" className="text-[#A855F7]" />
            <span className="text-[11px] tracking-[0.2em] uppercase text-[#A855F7] font-semibold">Auto-Build · GPT-5.2</span>
          </div>
          <h1 className="text-4xl font-[Outfit] font-bold tracking-tight mb-2">{t('autobuild.title')}</h1>
          <p className="text-white/60 max-w-2xl">
            {t('autobuild.subtitle')}
          </p>
        </div>

        {/* Form */}
        <form onSubmit={generate} className="p-6 bg-[#0A0A0A] border border-white/10 rounded-2xl mb-6" data-testid="auto-build-form">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
            <div className="lg:col-span-5">
              <label className="block text-xs uppercase tracking-[0.2em] text-white/40 mb-2">{t('autobuild.form_niche_label')} *</label>
              <div className="relative">
                <Target size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                <input
                  type="text"
                  value={niche}
                  onChange={(e) => setNiche(e.target.value)}
                  placeholder={t('autobuild.form_niche_placeholder')}
                  data-testid="auto-build-niche-input"
                  className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 rounded-lg text-sm focus:outline-none focus:border-[#A855F7]/50 transition"
                  required
                />
              </div>
            </div>
            <div className="lg:col-span-7">
              <label className="block text-xs uppercase tracking-[0.2em] text-white/40 mb-2">{t('autobuild.form_offer_label')} *</label>
              <input
                type="text"
                value={offer}
                onChange={(e) => setOffer(e.target.value)}
                placeholder={t('autobuild.form_offer_placeholder')}
                data-testid="auto-build-offer-input"
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-sm focus:outline-none focus:border-[#A855F7]/50 transition"
                required
              />
            </div>
            <div className="lg:col-span-6">
              <label className="block text-xs uppercase tracking-[0.2em] text-white/40 mb-2">URL do funil concorrente (opcional)</label>
              <div className="relative">
                <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                <input
                  type="url"
                  value={sourceUrl}
                  onChange={(e) => setSourceUrl(e.target.value)}
                  placeholder="https://..."
                  data-testid="auto-build-url-input"
                  className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 rounded-lg text-sm focus:outline-none focus:border-[#A855F7]/50 transition"
                />
              </div>
            </div>
            <div className="lg:col-span-3">
              <label className="block text-xs uppercase tracking-[0.2em] text-white/40 mb-2">Idioma</label>
              <div className="relative">
                <Translate size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40 pointer-events-none" />
                <select
                  value={language}
                  onChange={(e) => setLanguage(e.target.value)}
                  data-testid="auto-build-language-select"
                  className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 rounded-lg text-sm focus:outline-none focus:border-[#A855F7]/50 transition appearance-none cursor-pointer"
                >
                  {LANGUAGES.map((l) => <option key={l.code} value={l.code} className="bg-[#0A0A0A]">{l.label}</option>)}
                </select>
              </div>
            </div>
            <div className="lg:col-span-3">
              <label className="block text-xs uppercase tracking-[0.2em] text-white/40 mb-2">{t('autobuild.form_tone_label')}</label>
              <select
                value={tone}
                onChange={(e) => setTone(e.target.value)}
                data-testid="auto-build-tone-select"
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-sm focus:outline-none focus:border-[#A855F7]/50 transition appearance-none cursor-pointer"
              >
                {TONES.map((tn) => <option key={tn.code} value={tn.code} className="bg-[#0A0A0A]">{tn.label}</option>)}
              </select>
            </div>
            <div className="lg:col-span-12">
              <button
                type="submit"
                disabled={loading || !niche.trim() || !offer.trim()}
                data-testid="auto-build-generate-btn"
                className="w-full px-5 py-3 bg-gradient-to-r from-[#A855F7] to-[#7C3AED] hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg text-sm font-semibold transition flex items-center justify-center gap-2 shadow-lg shadow-purple-500/20"
              >
                {loading ? (
                  <><Lightning size={16} weight="fill" className="animate-pulse" /> {t('autobuild.generating')}</>
                ) : (
                  <><Sparkle size={16} weight="fill" /> {t('autobuild.generate_btn')}</>
                )}
              </button>
            </div>
          </div>
        </form>

        {/* Loading */}
        {loading && !build && (
          <div className="p-10 bg-[#0A0A0A] border border-white/10 rounded-2xl text-center" data-testid="auto-build-loading">
            <Lightning size={32} weight="fill" className="mx-auto mb-4 text-[#A855F7] animate-pulse" />
            <p className="text-white/70 mb-1">{t('autobuild.loading_pre')} <strong className="text-white">{t('autobuild.loading_strong')}</strong>...</p>
            <p className="text-white/40 text-sm">{t('autobuild.loading_sub')}</p>
          </div>
        )}

        {/* Result */}
        {build && !loading && (
          <div className="space-y-4">
            {/* Build header */}
            <div className="p-5 bg-gradient-to-br from-[#A855F7]/10 to-transparent border border-[#A855F7]/30 rounded-2xl" data-testid="auto-build-result-header">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-[#A855F7] font-semibold mb-1">Funil gerado</div>
                  <h2 className="text-2xl font-[Outfit] font-bold mb-1">{build.niche}</h2>
                  <p className="text-sm text-white/60 line-clamp-2">{build.offer}</p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <button
                    onClick={() => downloadMarkdown(build)}
                    data-testid="auto-build-export-md"
                    className="inline-flex items-center gap-1.5 px-3 py-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg text-xs uppercase tracking-wider transition"
                  >
                    <Download size={13} weight="bold" /> Markdown
                  </button>
                  <button
                    onClick={() => downloadJson(build, `auto-build-${build.build_id}.json`)}
                    data-testid="auto-build-export-json"
                    className="inline-flex items-center gap-1.5 px-3 py-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg text-xs uppercase tracking-wider transition"
                  >
                    <Code size={13} weight="bold" /> JSON
                  </button>
                  <NotionExportButton
                    shape_type="auto_build"
                    source_id={build.build_id}
                    testId="auto-build-export-notion"
                    variant="ghost"
                  />
                  <TrelloExportButton
                    shape_type="auto_build"
                    source_id={build.build_id}
                    testId="auto-build-export-trello"
                    variant="ghost"
                  />
                  <ClickUpExportButton
                    shape_type="auto_build"
                    source_id={build.build_id}
                    testId="auto-build-export-clickup"
                    variant="ghost"
                  />
                  <button
                    onClick={() => navigate(`/ai-composer?build=${build.build_id}`)}
                    data-testid="auto-build-to-composer-btn"
                    className="inline-flex items-center gap-1.5 px-3 py-2 bg-gradient-to-r from-[#2563EB] to-[#3B82F6] hover:opacity-90 rounded-lg text-xs uppercase tracking-wider font-semibold transition"
                  >
                    <Sparkle size={13} weight="fill" /> IA Composer
                  </button>
                </div>
              </div>
            </div>

            {/* Tabs */}
            <div className="flex items-center gap-1 p-1 bg-[#0A0A0A] border border-white/10 rounded-xl w-fit" data-testid="auto-build-tabs">
              {tabs.map((tb) => {
                const Icon = tb.icon;
                const active = activeTab === tb.id;
                return (
                  <button
                    key={tb.id}
                    onClick={() => setActiveTab(tb.id)}
                    data-testid={`auto-build-tab-${tb.id}`}
                    className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold transition ${
                      active ? 'bg-white/10 text-white' : 'text-white/50 hover:text-white/80'
                    }`}
                    style={active ? { boxShadow: `inset 0 0 0 1px ${tb.color}44` } : {}}
                  >
                    <Icon size={14} weight={active ? 'fill' : 'bold'} style={{ color: active ? tb.color : undefined }} />
                    {tb.label}
                  </button>
                );
              })}
            </div>

            {/* Tab content */}
            {activeTab === 'flow' && (
              <div data-testid="auto-build-flow-tab" className="space-y-3">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div>
                    <h3 className="text-lg font-[Outfit] font-bold">{t('autobuild.flow_title')}</h3>
                    <p className="text-xs text-white/50">{t('autobuild.flow_caption')}</p>
                  </div>
                </div>
                <FunnelFlowchart
                  stages={autoBuildToStages(build)}
                  height="500px"
                  accent="#A855F7"
                  downloadName={`auto-build-${build.build_id}`}
                  testId="auto-build-flowchart"
                />
              </div>
            )}
            {activeTab === 'lead_magnet' && (
              <SectionCard
                icon={Star}
                color="#10B981"
                title="Lead Magnet"
                actions={
                  <button
                    onClick={() => copyToClipboard(JSON.stringify(build.lead_magnet, null, 2), i18n.t('autobuild.lm_copied'))}
                    data-testid="auto-build-copy-lm"
                    className="inline-flex items-center gap-1 px-2.5 py-1 bg-white/5 hover:bg-white/10 border border-white/10 rounded text-[10px] uppercase tracking-wider transition"
                  >
                    <Copy size={11} weight="bold" /> Copiar
                  </button>
                }
              >
                <LeadMagnetView data={build.lead_magnet} />
              </SectionCard>
            )}
            {activeTab === 'landing' && (
              <SectionCard
                icon={FileText}
                color="#2563EB"
                title="Landing Page Copy"
                actions={
                  <button
                    onClick={() => copyToClipboard(JSON.stringify(build.landing_copy, null, 2), i18n.t('autobuild.landing_copied'))}
                    data-testid="auto-build-copy-lp"
                    className="inline-flex items-center gap-1 px-2.5 py-1 bg-white/5 hover:bg-white/10 border border-white/10 rounded text-[10px] uppercase tracking-wider transition"
                  >
                    <Copy size={11} weight="bold" /> Copiar
                  </button>
                }
              >
                <LandingView data={build.landing_copy} />
              </SectionCard>
            )}
            {activeTab === 'emails' && (
              <SectionCard
                icon={Envelope}
                color="#A855F7"
                title="Sequência de Emails (7 emails)"
              >
                <EmailsView data={build.email_sequence} />
              </SectionCard>
            )}
          </div>
        )}

        {/* History */}
        {!build && !loading && history.length > 0 && (
          <div className="mt-8" data-testid="auto-build-history">
            <h3 className="text-sm uppercase tracking-[0.2em] text-white/40 mb-3">{t('autobuild.history_title')}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {history.map((h) => (
                <div key={h.build_id} className="p-4 bg-[#121212] border border-white/10 rounded-xl hover:border-white/20 transition group" data-testid={`auto-build-history-${h.build_id}`}>
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="flex items-center gap-1.5 min-w-0">
                      <Tag size={13} weight="bold" className="text-[#A855F7] flex-shrink-0" />
                      <span className="text-xs text-white/40 uppercase tracking-wider truncate">{h.language?.toUpperCase()}</span>
                    </div>
                    <button
                      onClick={() => deleteBuild(h.build_id)}
                      data-testid={`auto-build-history-delete-${h.build_id}`}
                      className="opacity-0 group-hover:opacity-100 text-white/30 hover:text-red-400 transition"
                      title="Apagar"
                    >
                      <Trash size={13} weight="bold" />
                    </button>
                  </div>
                  <button
                    onClick={() => openBuild(h.build_id)}
                    data-testid={`auto-build-history-open-${h.build_id}`}
                    className="text-left w-full"
                  >
                    <div className="font-[Outfit] font-bold text-white truncate mb-1">{h.niche}</div>
                    <div className="text-xs text-white/50 line-clamp-2 leading-snug mb-2">{h.offer}</div>
                    <div className="text-[10px] text-white/30 uppercase tracking-wider">{new Date(h.created_at).toLocaleDateString('pt-PT')}</div>
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Empty state when no history yet */}
        {!build && !loading && history.length === 0 && (
          <div className="p-8 bg-[#121212] border border-white/10 rounded-2xl text-center" data-testid="auto-build-empty">
            <Wrench size={32} weight="bold" className="mx-auto mb-3 text-white/30" />
            <h3 className="font-[Outfit] font-bold text-lg mb-1">{t('autobuild.history_title')}</h3>
            <p className="text-white/60 text-sm">{t('autobuild.history_empty')}</p>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default AutoBuild;
